/**
 * Client billing review API service (invoicing-ops Phase 4, reviewer-light).
 * One review row per client per month; identity is the natural key
 * (entity + period), so upsert is a PUT with no id.
 * @module lib/api/billing-review.api
 */

import type {
  ApiRequestConfig,
  ClientBillingReview,
  UpsertClientBillingReviewInput,
} from '@scrambl/shared'

import { BaseApi } from './base-api'
import { createSingletonApi } from './singleton-factory'

class BillingReviewApiService extends BaseApi<
  ClientBillingReview,
  UpsertClientBillingReviewInput,
  Partial<UpsertClientBillingReviewInput>
> {
  constructor() {
    super({
      baseEndpoint: '/client-billing-reviews',
      responseNormalization: true,
    })
  }

  listForPeriod(
    params: {
      period: string
      entityId?: string
      assignedToMe?: boolean
    },
    options: Pick<ApiRequestConfig, 'signal' | 'headers'> = {},
  ): Promise<ClientBillingReview[]> {
    const query = new URLSearchParams({ period: params.period })
    if (params.entityId) query.set('entityId', params.entityId)
    if (params.assignedToMe) query.set('assignedToMe', 'true')
    return this.makeRequest<ClientBillingReview[]>(
      `${this.config.baseEndpoint}?${query.toString()}`,
      options,
    )
  }

  upsert(
    input: UpsertClientBillingReviewInput,
    options: Pick<ApiRequestConfig, 'signal' | 'headers'> = {},
  ): Promise<ClientBillingReview> {
    return this.makeRequest<ClientBillingReview>(this.config.baseEndpoint, {
      ...options,
      method: 'PUT',
      body: input,
    })
  }
}

export const billingReviewApi = createSingletonApi(BillingReviewApiService)
